import { useHistory } from 'react-router'
import { generatePath } from 'react-router-dom'
import { Button } from 'shared/components/Button/Button'
import { Text } from 'shared/components/Text/Text'
import { formatMonths, getDateFormatYearMonth } from 'shared/helpers/formatters'
import { cyanD1, darkGray, midGray, primaryBlack } from 'shared/theme/colors'

import { useBreakpoint } from '../../../../hooks/useBreakpoint'
import { EmploymentJob, EmploymentJobType } from '../../../../models/employment'
import { FlexBox } from '../../../Base/FlexBox'
import { EMPLOYMENT_ROUTES } from '../EmploymentHistory.constants'
import {
  EmploymentHistoryItemAction,
  EmploymentHistoryItemActions,
  EmploymentHistoryItemColumn,
  EmploymentHistoryItemContainer,
  EmploymentHistoryItemIconButton,
  EmploymentHistoryItemLineColumn,
  EmploymentHistoryItemLineVerticalLine,
  EmploymentHistoryItemMonthGapText,
  EmploymentHistoryItemStyledDescriptionIcon,
  EmploymentHistoryItemStyledEditIcon,
  EmploymentHistoryItemStyledErrorOutlineIcon,
  EmploymentHistoryItemStyledFiberManualRecord,
  EmploymentHistoryItemWrapper,
} from './EmploymentHistoryItem.styles'

interface EmploymentHistoryItemProps {
  item: EmploymentJob
  idx: number
  gapInMonths: number | null
  gapChildren?: React.ReactNode
}

export function EmploymentHistoryItem({ item, idx, gapInMonths, gapChildren }: EmploymentHistoryItemProps) {
  const isMobile = useBreakpoint('xs')
  const isDriving = item.jobType === EmploymentJobType.DRIVING
  const history = useHistory()
  const onEdit = () =>
    history.push(generatePath(EMPLOYMENT_ROUTES.ONBOARDING_EMPLOYMENT_HISTORY_EDIT, { id: item.id }))

  return (
    <EmploymentHistoryItemWrapper $hasMarginBottom={gapInMonths === null}>
      <EmploymentHistoryItemContainer>
        <EmploymentHistoryItemLineColumn>
          <EmploymentHistoryItemLineVerticalLine $hidden={idx === 0} />
          {item.isComplete ? (
            <EmploymentHistoryItemStyledFiberManualRecord />
          ) : (
            <EmploymentHistoryItemStyledErrorOutlineIcon />
          )}
          <EmploymentHistoryItemLineVerticalLine $flex $hidden={gapInMonths === null} />
        </EmploymentHistoryItemLineColumn>
        <EmploymentHistoryItemColumn $middle>
          <Text variant="uppercaseLabel3" fontColor={midGray} mb={1}>
            {getDateFormatYearMonth(item.details.startDateObject)} -{' '}
            {item.details.isCurrentlyWorkingHere
              ? 'Present'
              : item.details.endDateObject
              ? getDateFormatYearMonth(item.details.endDateObject)
              : null}
          </Text>
          <Text variant="label3" fontColor={primaryBlack}>
            {item.details.companyName}
          </Text>
          <Text variant="header4" fontColor={isDriving ? cyanD1 : darkGray} mt={1}>
            {isDriving ? 'Class 8 driving job' : item.details.positionHeld}
          </Text>
        </EmploymentHistoryItemColumn>
        <EmploymentHistoryItemColumn $alignItems="flex-end">
          <EmploymentHistoryItemActions>
            {item.driverDocuments.length ? (
              <EmploymentHistoryItemAction $justifyContent="flex-end">
                <EmploymentHistoryItemIconButton
                  onClick={() => window.open(item.driverDocuments[item.driverDocuments.length - 1].url)}
                >
                  <EmploymentHistoryItemStyledDescriptionIcon />
                </EmploymentHistoryItemIconButton>
              </EmploymentHistoryItemAction>
            ) : null}
            {item.canEdit ? (
              <>
                {item.isComplete ? (
                  <EmploymentHistoryItemAction $justifyContent="flex-end">
                    <EmploymentHistoryItemIconButton onClick={onEdit}>
                      <EmploymentHistoryItemStyledEditIcon />
                    </EmploymentHistoryItemIconButton>
                  </EmploymentHistoryItemAction>
                ) : (
                  <Button variant="secondary" size="medium" onClick={onEdit}>
                    Continue
                  </Button>
                )}
              </>
            ) : null}
          </EmploymentHistoryItemActions>
        </EmploymentHistoryItemColumn>
      </EmploymentHistoryItemContainer>
      <EmploymentHistoryItemContainer $noBackgroundColor>
        <EmploymentHistoryItemLineColumn $isGap>
          <EmploymentHistoryItemLineVerticalLine
            $flex
            $dashed={Boolean(gapInMonths)}
            $hidden={gapInMonths === null}
          />
        </EmploymentHistoryItemLineColumn>
        {gapInMonths ? (
          <>
            <EmploymentHistoryItemMonthGapText variant="header4">
              <FlexBox
                styles="width: 100%;"
                justifyContent="center"
                alignItems="center"
                gap="10px"
                ml={1}
                isColumn={isMobile}
              >
                {formatMonths(gapInMonths)} gap
                {gapChildren}
              </FlexBox>
            </EmploymentHistoryItemMonthGapText>
          </>
        ) : null}
      </EmploymentHistoryItemContainer>
    </EmploymentHistoryItemWrapper>
  )
}
